import { useEffect, useRef, useState } from 'react'
import {
  Download,
  FileText,
  Loader2,
  Search,
  CheckCircle2,
  Database,
  FileSearch,
  Filter,
  Shuffle,
  Layers,
  ScanSearch,
  Brain,
} from 'lucide-react'

interface OperationStep {
  action: string
  status: 'start' | 'done'
  details: Record<string, any>
}

interface Props {
  steps: OperationStep[]
  lang?: 'zh' | 'en'
  isStreaming?: boolean
}

const ACTION_CONFIG: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>
    labelZh: string
    labelEn: string
    thinkingZh: string
    thinkingEn: string
    subZh?: string
    subEn?: string
  }
> = {
  load_attachments: {
    icon: FileText,
    labelZh: '加载附件',
    labelEn: 'Load Attachments',
    thinkingZh: '正在加载附件…',
    thinkingEn: 'Loading attachments…',
  },
  scan_kb: {
    icon: ScanSearch,
    labelZh: '全库扫描',
    labelEn: 'Full KB Scan',
    thinkingZh: '正在全库扫描…',
    thinkingEn: 'Scanning knowledge base…',
  },
  recall: {
    icon: Search,
    labelZh: '混合召回',
    labelEn: 'Hybrid Recall',
    thinkingZh: '正在混合召回…',
    thinkingEn: 'Running hybrid recall…',
    subZh: '向量语义 + 法律关键词双路召回',
    subEn: 'Vector semantic + legal keyword dual-channel recall',
  },
  filter: {
    icon: Filter,
    labelZh: '相关度过滤',
    labelEn: 'Relevance Filter',
    thinkingZh: '正在过滤相关度…',
    thinkingEn: 'Filtering by relevance…',
  },
  rerank: {
    icon: Shuffle,
    labelZh: '多样性重排',
    labelEn: 'Diversity Rerank',
    thinkingZh: '正在重排序…',
    thinkingEn: 'Reranking for diversity…',
  },
  build_context: {
    icon: Layers,
    labelZh: '构建上下文',
    labelEn: 'Build Context',
    thinkingZh: '正在构建上下文…',
    thinkingEn: 'Building context…',
  },
  search_kb: {
    icon: Search,
    labelZh: '检索知识库',
    labelEn: 'Search Knowledge Base',
    thinkingZh: '正在检索知识库…',
    thinkingEn: 'Searching knowledge base…',
  },
  build_prompt: {
    icon: Database,
    labelZh: '构建上下文',
    labelEn: 'Build Context',
    thinkingZh: '正在构建上下文…',
    thinkingEn: 'Building context…',
  },
  generate: {
    icon: Loader2,
    labelZh: '生成回答',
    labelEn: 'Generate Answer',
    thinkingZh: '正在生成回答…',
    thinkingEn: 'Generating answer…',
  },
}

interface DocHit {
  source_type: string
  source_id: string
  title: string
  n_chunks: number
  best_score: number
}

/* ── Per-action detail renderers ── */

function fmtElapsed(s: number): string {
  if (s < 0.001) return '<0.001s'
  return `${s.toFixed(4)}s`
}

function ScanKbDetails({ details, lang, isDone }: { details: Record<string, any>; lang: string; isDone: boolean }) {
  if (!isDone) {
    const total = details.total_files || 0
    return (
      <div className="mt-0.5 text-[11px] text-blue-600">
        {lang === 'zh'
          ? `正在遍历知识库全部 ${total} 份文件，提取向量与关键词索引…`
          : `Scanning all ${total} files in knowledge base…`}
      </div>
    )
  }
  const totalFiles = details.total_files
  const totalChunks = details.total_chunks
  const matchedDocs = details.matched_docs
  const elapsed = details.elapsed_s
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-green-700">
      {totalFiles !== undefined && totalFiles > 0 && (
        <span>{lang === 'zh' ? '全库文件' : 'Total files'}: <b>{totalFiles}</b></span>
      )}
      {totalChunks !== undefined && totalChunks > 0 && (
        <span>{lang === 'zh' ? '总分块' : 'Total chunks'}: <b>{totalChunks}</b></span>
      )}
      {matchedDocs !== undefined && (
        <span>{lang === 'zh' ? '命中文档' : 'Docs matched'}: <b>{matchedDocs}</b></span>
      )}
      {elapsed !== undefined && <span className="opacity-60">{fmtElapsed(elapsed)}</span>}
    </div>
  )
}

function RecallDetails({ details, lang, isDone }: { details: Record<string, any>; lang: string; isDone: boolean }) {
  if (!isDone) {
    return (
      <div className="mt-0.5 text-[11px] text-blue-600">
        {lang === 'zh'
          ? `正在执行 ${details.mode === 'hybrid' ? '向量语义 + 法律关键词双路召回' : details.mode}检索…`
          : `Running ${details.mode} retrieval…`}
      </div>
    )
  }
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-green-700">
      <span>{lang === 'zh' ? '召回文本分块' : 'Chunks'}: <b>{details.raw_hits}</b></span>
      {details.elapsed_s !== undefined && <span className="opacity-60">{fmtElapsed(details.elapsed_s)}</span>}
    </div>
  )
}

function FilterDetails({ details, lang, isDone }: { details: Record<string, any>; lang: string; isDone: boolean }) {
  if (!isDone) {
    return (
      <div className="mt-0.5 text-[11px] text-blue-600">
        {lang === 'zh'
          ? `正在过滤低于 ${details.threshold} 相似度的文本分块…`
          : `Filtering chunks below ${details.threshold} similarity…`}
      </div>
    )
  }
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-green-700">
      <span>{lang === 'zh' ? '输入' : 'Input'}: <b>{details.input_chunks}</b></span>
      <span>{lang === 'zh' ? '保留' : 'Kept'}: <b>{details.kept_chunks}</b></span>
      {details.dropped_chunks > 0 && (
        <span className="text-amber-600">{lang === 'zh' ? '过滤丢弃' : 'Dropped'}: {details.dropped_chunks}</span>
      )}
      {details.elapsed_s !== undefined && <span className="opacity-60">{fmtElapsed(details.elapsed_s)}</span>}
    </div>
  )
}

function RerankDetails({ details, lang, isDone }: { details: Record<string, any>; lang: string; isDone: boolean }) {
  if (!isDone) {
    return (
      <div className="mt-0.5 text-[11px] text-blue-600">
        {lang === 'zh'
          ? `正在按文档去重，每文档最多保留 ${details.max_per_doc} 个文本分块…`
          : `Diversifying, max ${details.max_per_doc} chunks per doc…`}
      </div>
    )
  }
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-green-700">
      <span>{lang === 'zh' ? '输入' : 'Input'}: <b>{details.input_chunks}</b></span>
      <span>{lang === 'zh' ? '保留' : 'Kept'}: <b>{details.kept_chunks}</b></span>
      {details.elapsed_s !== undefined && <span className="opacity-60">{fmtElapsed(details.elapsed_s)}</span>}
    </div>
  )
}

function downloadSourceFile(sourceType: string, sourceId: string, fallbackName: string) {
  const url = sourceType === 'user_kb'
    ? `/knowledge/user-knowledge/${sourceId}/file`
    : `/knowledge/legal-documents/${sourceId}/file`
  fetch(url)
    .then((r) => {
      const cd = r.headers.get('Content-Disposition') || ''
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
      const downloadName = match ? decodeURIComponent(match[1]) : fallbackName
      return r.blob().then((blob) => ({ blob, downloadName }))
    })
    .then(({ blob, downloadName }) => {
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = downloadName
      a.click()
      URL.revokeObjectURL(objUrl)
    })
    .catch(() => {})
}

function BuildContextDetails({ details, lang, isDone }: { details: Record<string, any>; lang: string; isDone: boolean }) {
  if (!isDone) {
    return (
      <div className="mt-0.5 text-[11px] text-blue-600">
        {lang === 'zh'
          ? `正在组装上下文：${details.chunks} 个文本分块 + ${details.attachments} 附件 + ${details.history_turns} 轮历史…`
          : `Assembling context: ${details.chunks} chunks + ${details.attachments} attachments + ${details.history_turns} history turns…`}
      </div>
    )
  }
  const docs: DocHit[] = details.documents || []
  const dedupDocs = details.dedup_docs ?? docs.length
  const sources = details.sources ?? 0
  const attachments = details.attachments ?? 0
  return (
    <div className="mt-1 text-xs space-y-1.5">
      {/* Plain-language summary */}
      <div className="text-[11px] text-green-800 font-medium">
        {lang === 'zh'
          ? <>找到 <b>{dedupDocs}</b> 份相关文档（共 <b>{sources}</b> 段文字摘录{attachments > 0 ? `，附加 ${attachments} 份案件文件` : ''}）用于本次解答</>
          : <>Found <b>{dedupDocs}</b> relevant document{dedupDocs !== 1 ? 's' : ''} across <b>{sources}</b> text excerpt{sources !== 1 ? 's' : ''}{attachments > 0 ? ` + ${attachments} case file${attachments !== 1 ? 's' : ''}` : ''} to answer your question</>
        }
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-green-600/70">
        <span>~{details.prompt_tokens_est}{lang === 'zh' ? ' tokens' : ' tok'}</span>
        {details.elapsed_s !== undefined && <span className="opacity-60">{fmtElapsed(details.elapsed_s)}</span>}
      </div>
      {docs.length > 0 && (
        <div className="space-y-0.5 pt-1 border-t border-green-100">
          <div className="text-[10px] font-medium text-green-600/70 mb-0.5">
            {lang === 'zh' ? '最相关来源' : 'Most relevant sources'}
          </div>
          {docs.map((doc) => (
            <div key={`${doc.source_type}-${doc.source_id}`} className="flex items-center gap-1.5 pl-1.5 text-green-700/80 hover:bg-green-50 rounded transition-colors group">
              <FileSearch className="w-3 h-3 flex-shrink-0" />
              <span className="truncate flex-1">{doc.title || doc.source_id}</span>
              <span className="flex-shrink-0 opacity-70">{doc.n_chunks}{lang === 'zh' ? '分块' : 'c'}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  downloadSourceFile(doc.source_type, doc.source_id, doc.title || doc.source_id)
                }}
                className="p-0.5 rounded text-green-600 opacity-0 group-hover:opacity-100 hover:bg-green-200 transition-all flex-shrink-0"
                title={lang === 'zh' ? '下载源文件' : 'Download source file'}
              >
                <Download className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const DETAIL_RENDERERS: Record<string, React.ComponentType<{ details: Record<string, any>; lang: string; isDone: boolean }>> = {
  scan_kb: ScanKbDetails,
  recall: RecallDetails,
  filter: FilterDetails,
  rerank: RerankDetails,
  build_context: BuildContextDetails,
}

const STEP_REVEAL_DELAY = 1200 // ms per step thinking buffer

export function OperationSteps({ steps, lang = 'zh', isStreaming = false }: Props) {
  // Collapse start+done pairs: one row per action, done overrides start.
  const actionOrder: string[] = []
  const merged = new Map<string, OperationStep>()
  for (const step of steps || []) {
    if (!merged.has(step.action)) {
      actionOrder.push(step.action)
      merged.set(step.action, step)
    } else if (step.status === 'done') {
      const prev = merged.get(step.action)!
      merged.set(step.action, {
        ...step,
        details: { ...(prev.details || {}), ...step.details },
      })
    }
  }

  // ── Sequential reveal ──
  // revealCount = how many steps (by index in actionOrder) are visible.
  // - Historical (not streaming): reveal all instantly
  // - Streaming: reveal one at a time with STEP_REVEAL_DELAY between each
  const [revealCount, setRevealCount] = useState(isStreaming ? 0 : actionOrder.length)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    // Non-streaming: show everything immediately
    if (!isStreaming) {
      setRevealCount(actionOrder.length)
      return
    }

    // Streaming: if there are more steps to reveal and no timer running, start one
    if (revealCount < actionOrder.length && !timerRef.current) {
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        setRevealCount((c) => c + 1)
      }, STEP_REVEAL_DELAY)
    }

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [revealCount, actionOrder.length, isStreaming])

  if (!steps || steps.length === 0) return null

  // Only number the main pipeline steps
  const PIPELINE_STEPS = ['scan_kb', 'recall', 'filter', 'rerank', 'build_context']
  let stepNum = 0

  return (
    <div className="text-sm">
      {actionOrder.map((action, idx) => {
        const step = merged.get(action)!
        const config = ACTION_CONFIG[action] || { icon: FileText, labelZh: action, labelEn: action }
        const Icon = config.icon
        const label = lang === 'zh' ? config.labelZh : config.labelEn
        const isRevealed = idx < revealCount
        const isDone = step.status === 'done'
        const DetailRenderer = DETAIL_RENDERERS[action]
        const isPipeline = PIPELINE_STEPS.includes(action)
        if (isPipeline) stepNum++
        const sub = lang === 'zh' ? config.subZh : config.subEn
        const isLast = idx === actionOrder.length - 1

        return (
          <div key={action} className="relative">
            {/* Connection line to next step */}
            {isPipeline && !isLast && (
              <div className="absolute left-[15px] top-7 bottom-0 w-px bg-brand-200" />
            )}
            <div
              className={`flex items-start gap-2 px-2.5 py-1.5 rounded-md transition-all duration-300 relative ${
                isRevealed
                  ? (isDone ? 'bg-green-50/70 text-green-800' : 'bg-blue-50/70 text-blue-700')
                  : 'bg-brand-50/40 text-brand-400'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5 relative">
                {!isRevealed ? (
                  <Brain className="w-3.5 h-3.5 animate-pulse text-brand-400" />
                ) : isDone ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Icon className="w-3.5 h-3.5 animate-spin" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium leading-tight">
                  {isPipeline && isRevealed && (
                    <span className="text-brand-400 mr-1">{stepNum}.</span>
                  )}
                  {isRevealed
                    ? label
                    : (lang === 'zh' ? (config.thinkingZh || '正在思考…') : (config.thinkingEn || 'Thinking…'))}
                </div>
                {isRevealed && sub && (
                  <div className="text-[10px] text-brand-400 mt-0.5">{sub}</div>
                )}
                {isRevealed && DetailRenderer && step.details && (
                  <DetailRenderer details={step.details} lang={lang} isDone={isDone} />
                )}
                {isRevealed && !DetailRenderer && step.details && (
                  <div className="mt-0.5 text-[11px] opacity-70 space-y-0.5">
                    {step.details.files && (
                      <div>
                        {lang === 'zh' ? '文件' : 'Files'}:{' '}
                        {Array.isArray(step.details.files) ? step.details.files.join(', ') : step.details.files}
                      </div>
                    )}
                    {step.details.count !== undefined && (
                      <div>{lang === 'zh' ? '数量' : 'Count'}: {step.details.count}</div>
                    )}
                  </div>
                )}
                {!isRevealed && (
                  <div className="mt-0.5 flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-brand-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1 h-1 rounded-full bg-brand-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1 h-1 rounded-full bg-brand-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}